import { PerformanceMode } from './performanceMode';

// Configuration centralisée pour Realtime - réduit 5M+ appels realtime.list_changes

export const RealtimeConfig = {
  // Tables critiques qui NÉCESSITENT vraiment Realtime
  CRITICAL_TABLES: [
    'chat_messages',           // Messages en temps réel
    'notifications',           // Notifications instantanées
    'commission_join_requests', // Requêtes urgentes
    'commission_messages'      // Messages de commission en temps réel
  ],

  // Tables qui peuvent utiliser polling manuel au lieu de Realtime
  POLLING_TABLES: [
    'club_members',      // Polling toutes les 5 min suffit
    'events',            // Polling toutes les 10 min suffit
    'opportunities',     // Polling toutes les 10 min suffit
    'user_presence',     // Batch updates + polling 2 min
    'commission_members' // Polling toutes les 5 min suffit
  ],

  // Vérifier si Realtime doit être activé pour une table
  shouldEnableRealtime(tableName: string): boolean {
    // En mode performance, désactiver tout sauf les tables critiques
    if (PerformanceMode.isEnabled()) {
      return this.CRITICAL_TABLES.includes(tableName);
    }

    // En mode normal, activer pour les tables critiques uniquement
    // Les autres tables doivent utiliser polling manuel
    return this.CRITICAL_TABLES.includes(tableName);
  },

  // Intervalle de polling recommandé par table (en ms)
  getPollingInterval(tableName: string): number {
    const intervals: Record<string, number> = {
      'club_members': 5 * 60 * 1000,             // 5 minutes
      'events': 10 * 60 * 1000,                  // 10 minutes
      'opportunities': 10 * 60 * 1000,           // 10 minutes
      'user_presence': 2 * 60 * 1000,            // 2 minutes (+ batch updates)
      'commission_members': 5 * 60 * 1000,       // 5 minutes
      'connection_requests': 30 * 1000,          // 30 secondes
      'messages': 1 * 60 * 1000,                 // 1 minute
      'global_app_data_polling': 5 * 60 * 1000,  // 5 minutes
      'workflow_executions': 1 * 60 * 1000,      // 1 minute
      'commission_join_requests': 30 * 1000      // 30 secondes
    };

    return intervals[tableName] || 5 * 60 * 1000; // Défaut: 5 minutes
  },

  // Log pour debugging
  logRealtimeDecision(tableName: string, enabled: boolean) {
    if (enabled) {
      console.log(`✅ Realtime ACTIVÉ pour ${tableName} (table critique)`);
    } else {
      const interval = this.getPollingInterval(tableName);
      console.log(`⏱️ Realtime DÉSACTIVÉ pour ${tableName} - polling manuel: ${interval / 1000}s`);
    }
  }
};

// 🔍 DEBUG MODE: Activer pour tracer TOUS les appels Realtime
const DEBUG_REALTIME = true;

// Helper pour créer un système de polling manuel optimisé
export const createPollingInterval = (
  tableName: string,
  fetchFn: () => Promise<void>,
  enabled: boolean = true
): (() => void) => {
  if (DEBUG_REALTIME) {
    console.warn(`🔍 [REALTIME DEBUG] createPollingInterval called for: ${tableName}`);
    console.trace(); // Stack trace pour identifier l'appelant
  }

  if (!enabled || RealtimeConfig.shouldEnableRealtime(tableName)) {
    if (DEBUG_REALTIME) {
      console.log(`⏭️ Polling désactivé pour ${tableName} (Realtime actif ou polling désactivé)`);
    }
    return () => {}; // No-op
  }

  const interval = RealtimeConfig.getPollingInterval(tableName);
  console.log(`⏱️ Setting up polling for ${tableName} every ${interval / 1000}s`);

  const intervalId = setInterval(fetchFn, interval);

  return () => {
    clearInterval(intervalId);
    console.log(`🛑 Polling stopped for ${tableName}`);
  };
};

// 🔍 Wrapper pour tracer toutes les souscriptions Realtime
export const trackRealtimeChannel = (channelName: string, tableName: string) => {
  if (DEBUG_REALTIME) {
    console.warn(`🚨 [REALTIME SUBSCRIPTION] Channel: ${channelName}, Table: ${tableName}`);
    console.trace(); // Stack trace complète
    
    // Vérifier si la table devrait vraiment être en Realtime
    if (!RealtimeConfig.CRITICAL_TABLES.includes(tableName)) {
      console.error(`⚠️ WARNING: Table "${tableName}" uses Realtime but is NOT in CRITICAL_TABLES!`);
      console.error(`   Expected: [${RealtimeConfig.CRITICAL_TABLES.join(', ')}]`);
    }
  }
};
